import type { AdminVehicleGenerationDto } from '@carcheck/shared';
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { GenerationDialog } from './GenerationDialog';
import { GenerationRow } from './GenerationRow';
import { useModelGenerations } from '../../../api/admin-vehicle-generations';
import type { UseCatalogWriteResult } from '../_lib/useCatalogWrite';
import { useGenerationStatus } from '../_lib/useGenerationStatus';

/**
 * «Поколения · N · M видно в приложении» of the model dialog (AUT-450 /
 * VG-03). Newest first, hidden rows included (muted). Every write here saves
 * at once and is independent of the model form's «Сохранить». The add /
 * edit dialog reads its row from this list on every render, so a refetch
 * (after a stale 409) re-seeds it.
 */
export interface GenerationsSectionProps {
  modelId: string;
  /** «Toyota Camry» — the dialog subtitle. */
  modelTitle: string;
  write: UseCatalogWriteResult;
  disabled: boolean;
}

const K = 'vehicleCatalog.generations';
const LIST_MAX_HEIGHT = 360;

type Editing = { mode: 'add' } | { mode: 'edit'; id: string } | null;

const AddButton = (p: { disabled: boolean; onClick: () => void }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<AddIcon />}
      disabled={p.disabled}
      onClick={p.onClick}
    >
      {t(`${K}.add`)}
    </Button>
  );
};

const EmptyState = (p: { disabled: boolean; onAdd: () => void }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="body2" fontWeight={600}>
        {t(`${K}.emptyTitle`)}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
        {t(`${K}.emptyBody`)}
      </Typography>
      <AddButton disabled={p.disabled} onClick={p.onAdd} />
    </Paper>
  );
};

const GenerationList = (p: {
  items: readonly AdminVehicleGenerationDto[];
  disabled: boolean;
  onEdit: (g: AdminVehicleGenerationDto) => void;
  onToggleStatus: (g: AdminVehicleGenerationDto) => void;
}): ReactElement => (
  <Paper variant="outlined" sx={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto' }}>
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {p.items.map((g) => (
        <GenerationRow
          key={g.id}
          generation={g}
          disabled={p.disabled}
          onEdit={p.onEdit}
          onToggleStatus={p.onToggleStatus}
        />
      ))}
    </Box>
  </Paper>
);

export const GenerationsSection = (p: GenerationsSectionProps): ReactElement => {
  const { t } = useTranslation();
  const query = useModelGenerations(p.modelId);
  const setStatus = useGenerationStatus(p.write);
  const [editing, setEditing] = useState<Editing>(null);
  const items = query.data?.items ?? [];
  const visible = items.filter((g) => g.status === 'active').length;
  const busy = p.disabled || p.write.busy || query.isFetching;
  const row = editing?.mode === 'edit' ? (items.find((g) => g.id === editing.id) ?? null) : null;
  const add = (): void => setEditing({ mode: 'add' });

  return (
    <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="overline" color="text.secondary">
          {t(`${K}.title`)} · {items.length}
          {items.length > 0 ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, textTransform: 'none' }}>
              · {t(`${K}.visible`, { n: visible })}
            </Typography>
          ) : null}
        </Typography>
        {items.length > 0 ? <AddButton disabled={busy} onClick={add} /> : null}
      </Box>
      {items.length > 0 ? (
        <Typography variant="caption" color="text.secondary">
          {t(`${K}.hint`)}
        </Typography>
      ) : null}
      {query.isLoading ? <CircularProgress size={24} /> : null}
      {query.error && !query.data ? <Alert severity="error">{t(`${K}.loadError`)}</Alert> : null}
      {query.data && items.length === 0 ? <EmptyState disabled={busy} onAdd={add} /> : null}
      {items.length > 0 ? (
        <GenerationList
          items={items}
          disabled={busy}
          onEdit={(g) => setEditing({ mode: 'edit', id: g.id })}
          onToggleStatus={(g) => void setStatus(g, g.status === 'hidden' ? 'active' : 'hidden')}
        />
      ) : null}
      {editing && (editing.mode === 'add' || row) ? (
        <GenerationDialog
          modelId={p.modelId}
          modelTitle={p.modelTitle}
          row={row}
          items={items}
          syncing={query.isFetching}
          write={p.write}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Box>
  );
};
